import React, { useState, useEffect, useCallback } from "react";
import { adminFetch } from "../utils/adminApi";
import styles from "./GroundingUsageWidget.module.css";

/**
 * GroundingUsageWidget - Admin dashboard widget hiển thị Google Search grounding stats
 *
 * Plan: autowork-root/plans/260617-grounding-usage-firestore-stats
 *
 * Hiển thị:
 * - 4 cards tóm tắt: hôm nay (calls, queries, cost, points)
 * - Free quota warning (nếu sắp hết 5,000/tháng Gemini)
 * - 7-day trend table (calls, queries, cost mỗi ngày)
 * - Top 5 users
 */
const GroundingUsageWidget = () => {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminFetch(`/api/admin/grounding/stats?range=${range}`);
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || "Không thể tải thống kê");
      }
    } catch (err) {
      console.error("[GroundingUsageWidget] fetch error:", err);
      setError(err.message || "Lỗi kết nối");
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    fetchData();
    // Auto refresh mỗi 5 phút
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={styles.widget}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Đang tải thống kê grounding...</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={styles.widget}>
        <div className={styles.error}>
          <span>⚠️ {error}</span>
          <button onClick={fetchData} className={styles.retryBtn}>
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { today, monthToDate, trend, topUsers } = data;
  const hasData = (today?.totalCalls || 0) > 0;

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.icon}>🔎</span>
          <h3>Google Search Grounding Usage</h3>
        </div>
        <div className={styles.controls}>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className={styles.rangeSelect}
            aria-label="Chọn khoảng thời gian"
          >
            <option value="7d">7 ngày</option>
            <option value="30d">30 ngày</option>
            <option value="month">Tháng này</option>
          </select>
          <button
            onClick={fetchData}
            className={styles.refreshBtn}
            disabled={loading}
            aria-label="Làm mới"
          >
            ↻
          </button>
        </div>
      </div>

      {/* Free quota warning */}
      {monthToDate?.freeQuotaWarning && (
        <div className={styles.warningBanner} role="alert">
          <span className={styles.warningIcon}>⚠️</span>
          <div>
            <strong>Cảnh báo free quota Gemini:</strong> đã dùng{" "}
            {monthToDate.totalQueries.toLocaleString()} /{" "}
            {monthToDate.freeQuotaTotal.toLocaleString()} prompts trong tháng.{" "}
            {monthToDate.freeQuotaExhausted
              ? "ĐÃ HẾT free quota — chi phí $14/1,000 queries sẽ áp dụng."
              : `Còn ${monthToDate.freeQuotaRemaining.toLocaleString()} prompts free.`}
          </div>
        </div>
      )}

      {!hasData && (
        <div className={styles.emptyState}>
          <p>📊 Chưa có dữ liệu grounding trong khoảng thời gian này.</p>
          <p className={styles.emptyHint}>
            Dữ liệu sẽ xuất hiện khi user gói Cao Cấp/Doanh Nghiệp tạo bài viết có
            bật chế độ chuyên sâu (Google Search).
          </p>
        </div>
      )}

      {hasData && (
        <>
          {/* Today cards */}
          <div className={styles.cards}>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Calls hôm nay</div>
              <div className={styles.cardValue}>{today.totalCalls}</div>
              <div className={styles.cardSub}>
                {today.noSearchCalls > 0 && (
                  <span className={styles.warn}>
                    {today.noSearchCalls} không search
                  </span>
                )}
                {today.cacheHits > 0 && (
                  <span className={styles.good}>
                    {today.cacheHits} cache hit
                  </span>
                )}
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Queries hôm nay</div>
              <div className={styles.cardValue}>{today.totalQueries}</div>
              <div className={styles.cardSub}>
                Google Search queries phát sinh
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Cost hôm nay</div>
              <div className={styles.cardValue}>
                ${(today.estimatedCostUsd || 0).toFixed(2)}
              </div>
              <div className={styles.cardSub}>
                Ước lượng bill Google
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Điểm thu hôm nay</div>
              <div className={styles.cardValue}>
                {(today.totalPointsCharged || 0).toLocaleString()}
              </div>
              <div className={styles.cardSub}>
                Premium points từ user
              </div>
            </div>
          </div>

          {/* Month to date summary */}
          <div className={styles.summary}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Tháng này (MTD):</span>
              <span>
                <strong>{monthToDate.totalCalls.toLocaleString()}</strong> calls ·{" "}
                <strong>{monthToDate.totalQueries.toLocaleString()}</strong> queries
                · <strong>${monthToDate.estimatedCostUsd.toFixed(2)}</strong> ước
                lượng
              </span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Free quota:</span>
              <span>
                {monthToDate.totalQueries.toLocaleString()} /{" "}
                {monthToDate.freeQuotaTotal.toLocaleString()} (
                {Math.round(
                  (monthToDate.totalQueries / monthToDate.freeQuotaTotal) * 100
                )}
                %)
              </span>
            </div>
            <div className={styles.quotaBar}>
              <div
                className={`${styles.quotaFill} ${
                  monthToDate.freeQuotaWarning ? styles.quotaFillWarning : ""
                }`}
                style={{
                  width: `${Math.min(
                    100,
                    (monthToDate.totalQueries / monthToDate.freeQuotaTotal) * 100
                  )}%`,
                }}
              />
            </div>
          </div>

          {/* 7-day trend */}
          {trend && trend.length > 0 && (
            <div className={styles.trendSection}>
              <h4 className={styles.sectionTitle}>Xu hướng {range}</h4>
              <table className={styles.trendTable}>
                <thead>
                  <tr>
                    <th>Ngày</th>
                    <th>Calls</th>
                    <th>Queries</th>
                    <th>Cost</th>
                    <th>Điểm</th>
                  </tr>
                </thead>
                <tbody>
                  {trend
                    .slice()
                    .reverse()
                    .map((d) => (
                      <tr key={d.date}>
                        <td>{d.date}</td>
                        <td>{d.calls}</td>
                        <td>{d.queries}</td>
                        <td>${(d.cost || 0).toFixed(2)}</td>
                        <td>{(d.pointsCharged || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Top users */}
          {topUsers && topUsers.length > 0 && (
            <div className={styles.topUsersSection}>
              <h4 className={styles.sectionTitle}>Top users ({range})</h4>
              <table className={styles.trendTable}>
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Calls</th>
                    <th>Queries</th>
                    <th>Điểm</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.slice(0, 5).map((u) => (
                    <tr key={u.userId}>
                      <td className={styles.userIdCell} title={u.userId}>
                        {u.userId.slice(0, 8)}...
                      </td>
                      <td>{u.calls}</td>
                      <td>{u.queries}</td>
                      <td>{(u.pointsCharged || 0).toLocaleString()}</td>
                      <td>{u.lastSeen || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className={styles.footer}>
        <small>
          Dữ liệu từ Firestore counters · Cache 5 phút · Cập nhật lúc{" "}
          {new Date(data.generatedAt).toLocaleString("vi-VN")}
        </small>
      </div>
    </div>
  );
};

export default GroundingUsageWidget;
